import React, {useState, useEffect, useCallback, useMemo, useRef} from "react";
import {useTranslation} from "react-i18next";
import {inject, observer} from "mobx-react";
import {Form, Formik} from "formik";
import debounce from "lodash.debounce";
//local
import {RichEditor, templatesType, templatesLevel} from "components/RichEditor";
import {Modal} from "components/Modal";
import {Tag} from "components/Tag";
import {ButtonTransparent} from "components/buttons/ButtonTransparent";
import StatusesSelect from "components/selects/StatusesSelect";
import ButtonDelete from "components/buttons/ButtonDelete";
import {Preloader} from "components/Preloader";
import {Input} from "components/Input";
import FormikField from "components/FormikField";
import {CustomTooltip} from "components/CustomTooltip";
import {ConfirmModal} from "components/ConfirmModal";
import {EditorTemplateRow} from "./EditorTemplateRow";
import {IconChevronLeft, IconPlus, IconPapers} from "components/icons";
import {color} from "constants/color.consts";

const listLanguageKey = "EDITOR_TEMPLATES_MODAL_LIST_";
const itemLanguageKey = "EDITOR_TEMPLATES_MODAL_ITEM_";

export const EditorTemplatesModal = inject("store")(
  observer(({store: {nsi, buyer}, outsideData, onChangeTemplate, onClose}) => {
    const {t} = useTranslation();

    const searchInputRef = useRef(null);
    const listRef = useRef(null);

    const emptyTemplate = useMemo(
      () => ({
        name: "",
        type: outsideData?.type ?? templatesType.RFT,
        level: templatesLevel.ORG,
        content: outsideData?.content ?? "<p></p>",
      }),
      [outsideData]
    );

    const [editorTemplates, setEditorTemplates] = useState(null);
    const [types, setTypes] = useState(null);
    // const [levels, setLevels] = useState(null);
    const [filteredTypes, setFilteredTypes] = useState(null);
    const [editTemplateId, setEditTemplateId] = useState(null);
    const [editTemplate, setEditTemplate] = useState(emptyTemplate);
    const [searchField, setSearchField] = useState(null);
    const [saveLoading, setSaveLoading] = useState(false);

    const FilteredTypeTags = ({types, activeTypes, setActiveTypes}) =>
      types?.map((type) => (
        <Tag
          text={type}
          color={color.discreteText}
          noBackground={!activeTypes?.includes(type)}
          uppercase
          bold
          addClass="fz-10 mr-0 ml-2 cpointer"
          needTranslation
          onClick={() =>
            activeTypes?.includes(type)
              ? setActiveTypes(activeTypes.filter((t) => t !== type))
              : setActiveTypes([...activeTypes, type])
          }
        />
      ));

    const OutsideTemplateTag = () => (
      <Tag
        text={outsideData?.type}
        color={color.discreteText}
        uppercase
        bold
        small
        addClass="ml-2 fz-10"
        needTranslation
      />
    );

    const loading = useMemo(() => !editorTemplates, [editorTemplates]);
    const isNewTemplate = useMemo(
      () => editTemplateId === "new" || outsideData?.openType === "add",
      [editTemplateId, outsideData]
    );
    const isOutsideSelecting = outsideData?.openType === "select";
    const isOutsideCreating = outsideData?.openType === "add";
    const hasTemplate = useMemo(
      () => isNewTemplate || (editTemplateId && editTemplate?.id),
      [isNewTemplate, editTemplate, editTemplateId]
    );
    const hasUnsavedChanges = (values) =>
      JSON.stringify(values?.content) !== JSON.stringify(editTemplate?.content) ||
      values?.name !== editTemplate?.name ||
      values?.type !== editTemplate?.type ||
      values?.level !== editTemplate?.level;
    const isListTemplates = useMemo(
      () => !!editorTemplates?.templates?.length,
      [editorTemplates]
    );

    const getTemplates = useCallback(
      async (page, types) => {
        const params = {
          name: searchField,
          page: page ?? 1,
          type: isOutsideSelecting ? [outsideData.type] : types,
        };
        await buyer.getEditorTemplates(params).then((data) =>
          setEditorTemplates((prev) =>
            page
              ? {
                  ...data,
                  templates: [...prev.templates, ...data.templates],
                }
              : data
          )
        );
      },
      [searchField, isOutsideSelecting, outsideData, buyer]
    );

    const getTemplate = useCallback(
      async (id) => {
        const profile = await buyer.getEditorTemplate(id);
        setEditTemplate(profile);
        return profile;
      },
      [buyer]
    );

    const addNewTemplate = () => {
      setEditTemplateId("new");
      setEditTemplate(emptyTemplate);
    };

    const clearTemplate = () => {
      setEditTemplateId(null);
      setEditTemplate(null);
    };

    const handleRemoveTemplate = () =>
      ConfirmModal({
        title: t("EDITOR_TEMPLATES_MODAL_TEMPLATE_WANT_DELETE"),
        text: t("EDITOR_TEMPLATES_MODAL_TEMPLATE_CANT_RETURN"),
        type: "warning",
        onOk: removeTemplate,
      });

    const removeTemplate = async () => {
      await buyer.deleteEditorTemplate(editTemplateId);
      clearTemplate();
      await getTemplates(undefined, filteredTypes);
    };

    const handleSaveTemplate = async (values) => {
      try {
        setSaveLoading(true);
        isNewTemplate
          ? await buyer.createEditorTemplate(values)
          : await buyer.updateEditorTemplate(editTemplateId, values);
        clearTemplate();
        await getTemplates(undefined, filteredTypes);
        isOutsideCreating && onClose();
      } finally {
        setSaveLoading(false);
      }
    };

    const searchOnChange = debounce((e) => {
      const name = e?.target?.value?.trim();
      if (name?.length) setSearchField(name.replace(/^(\d{6})-(\d{4})$/, "$1$2"));
      else setSearchField(null);
    }, 400);

    const loadNextPageData = async () => {
      if (!(editorTemplates?.totalPages - 1 >= editorTemplates?.currentPage)) return;
      const nextPage = ++editorTemplates.currentPage;
      await getTemplates(nextPage, filteredTypes);
      listRef.current.addEventListener("scroll", handleScroll);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleScroll = () => {
      const scrollPosition = listRef.current.scrollTop + listRef.current.clientHeight;
      const maxScrollPosition = listRef.current.scrollHeight - 150;
      if (listRef.current && scrollPosition >= maxScrollPosition) {
        listRef.current.removeEventListener("scroll", handleScroll);
        loadNextPageData();
      }
    };

    const handleSelectTemplate = (id) =>
      ConfirmModal({
        title: t("EDITOR_TEMPLATES_MODAL_TEMPLATE_WANT_SELECT"),
        text: t("EDITOR_TEMPLATES_MODAL_TEMPLATE_CANT_SELECT"),
        type: "warning",
        onOk: () => selectTemplate(id),
      });

    const selectTemplate = (id) =>
      getTemplate(id).then((template) => {
        onChangeTemplate(template);
        onClose();
      });

    useEffect(() => {
      const container = listRef.current;
      if (container) {
        container.addEventListener("scroll", handleScroll);
        return () => container.removeEventListener("scroll", handleScroll);
      }
    }, [handleScroll]);

    useEffect(() => {
      !!filteredTypes && getTemplates(undefined, filteredTypes);
    }, [getTemplates, filteredTypes]);

    useEffect(() => {
      nsi.getEditorTemplateTypes().then((data) => {
        setTypes(data);
        setFilteredTypes(data);
      });
      // nsi.getEditorTemplateLevels().then(setLevels);
    }, [nsi]);

    useEffect(() => {
      editTemplateId && !isNewTemplate && getTemplate(editTemplateId);
    }, [editTemplateId, getTemplate, isNewTemplate]);

    return (
      <Modal
        onClose={onClose}
        width={900}
        boldTitle
        tightTitle
        noCenterContent
        titleIcon={<IconPapers addClass="mr-4" />}
        titleText={
          isOutsideCreating
            ? "EDITOR_TEMPLATES_MODAL_ADD_TITLE"
            : "EDITOR_TEMPLATES_MODAL_TITLE"
        }
        suffixTitleIcon={
          !hasTemplate &&
          (isOutsideSelecting ? (
            <OutsideTemplateTag />
          ) : (
            <FilteredTypeTags
              types={types}
              activeTypes={filteredTypes}
              setActiveTypes={setFilteredTypes}
            />
          ))
        }
        addClass="modal-editor-templates_wrapper"
        contentClassName="modal-editor-templates"
      >
        <div>
          {loading && <Preloader addClass="preloader250" />}
          {!loading && (
            <React.Fragment>
              {hasTemplate ? (
                <div className="modal-editor-templates__content pt-4">
                  {!isOutsideCreating && (
                    <ButtonTransparent
                      icon={<IconChevronLeft color={color.blue} />}
                      noBorder
                      uppercase
                      bold
                      small
                      addClass="pl-0"
                      name={t(`${itemLanguageKey}BACK_BUTTON`)}
                      onClick={clearTemplate}
                    />
                  )}
                  <Formik
                    initialValues={editTemplate}
                    enableReinitialize
                    onSubmit={handleSaveTemplate}
                  >
                    {({values, setFieldValue}) => (
                      <Form>
                        <div className="df-row-center mt-4 mb-4">
                          <FormikField
                            name="name"
                            small
                            boldText
                            width="300px"
                            height="40px"
                            placeholder={t(`${itemLanguageKey}NAME_PLACEHOLDER`)}
                            className="input mb-0 fz-20"
                            addClass="mb-0"
                          />
                          {!isNewTemplate && (
                            <ButtonDelete onClick={handleRemoveTemplate} activeOnHover />
                          )}
                          <span className="ml-4 mr-1 __uppercase fz-12 __bold">
                            {t(`${itemLanguageKey}TYPE_LABEL`)}
                          </span>
                          {isOutsideCreating ? (
                            <Tag
                              text={values?.type}
                              color={color.discreteText}
                              uppercase
                              bold
                              addClass="fz-10"
                              needTranslation
                            />
                          ) : (
                            <StatusesSelect
                              list={types}
                              itemColor="discreteText"
                              onChange={(value) => setFieldValue("type", value)}
                              defaultValue={values?.type}
                            />
                          )}
                          {/*<span className="ml-6 mr-1 __uppercase fz-12 __bold">
                            {t(`${itemLanguageKey}LEVEL_LABEL`)}
                          </span>
                          <StatusesSelect
                            list={levels}
                            itemColor='midGray'
                            onChange={(value) => setFieldValue("level", value)}
                            defaultValue={values?.level}
                          />*/}
                          <CustomTooltip
                            text={onChangeTemplate && `${itemLanguageKey}BUTTON_TOOLTIP`}
                            disabled={
                              isNewTemplate ? values?.name : hasUnsavedChanges(values)
                            }
                            centerText
                          >
                            <div className="pa-0 df mla">
                              <ButtonTransparent
                                name="SAVE_CHANGES"
                                medium
                                nonTransparent
                                disabled={
                                  !(isNewTemplate
                                    ? values?.name
                                    : hasUnsavedChanges(values))
                                }
                                loading={saveLoading}
                                onClick={() => handleSaveTemplate(values)}
                              />
                            </div>
                          </CustomTooltip>
                        </div>
                        <RichEditor
                          key={editTemplateId}
                          initialValue={values?.content ?? outsideData?.content}
                          mode="edit"
                          placeholder={t(`${itemLanguageKey}EDITOR_PLACEHOLDER`)}
                          needInputDebounce={false}
                          showExtendedToolbarButtons
                          onChange={(value) =>
                            !saveLoading && setFieldValue("content", value)
                          }
                        />
                        <div className="modal-editor-templates__profile-body"></div>
                      </Form>
                    )}
                  </Formik>
                </div>
              ) : (
                <div className="modal-editor-templates__content pt-4">
                  <Input
                    small
                    fullWidth
                    focus={searchInputRef}
                    autoFocus
                    defaultValue={searchField}
                    onChange={searchOnChange}
                    placeholder={t(`${listLanguageKey}INPUT_SEARCH_PLACEHOLDER`)}
                    showClearIcon
                    onClear={() => searchOnChange()}
                  />
                  {isListTemplates ? (
                    <div className="modal-editor-templates__table-body" ref={listRef}>
                      {editorTemplates.templates?.map((template) => (
                        <EditorTemplateRow
                          key={template.id}
                          template={template}
                          onEdit={setEditTemplateId}
                          onSelect={onChangeTemplate ? handleSelectTemplate : undefined}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="modal-editor-templates__table-body mt-4">
                      <div className="null-state-wrapper pa-3">
                        <div className="mb-2">{t(`${listLanguageKey}NO_DATA`)}</div>
                      </div>
                    </div>
                  )}

                  {!onChangeTemplate && (
                    <div className="df-row-jce-center mt-4">
                      <ButtonTransparent
                        name="EDITOR_TEMPLATES_MODAL_NEW_BUTTON"
                        small
                        icon={<IconPlus />}
                        onClick={addNewTemplate}
                      />
                    </div>
                  )}
                </div>
              )}
            </React.Fragment>
          )}
        </div>
      </Modal>
    );
  })
);
